// src/pages/wallet/TransferPage.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiSend, FiUser, FiDollarSign, FiFileText, FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import TextInput from "../../components/common/TextInput";
import Button from "../../components/common/Button";
import { transferMoney, getWalletOverview } from "../../api/walletApi";
import { useAuth } from "../../hooks/useAuth";
import {
  calculateFee,
  canTransferWithdrawAmount,
  STATUS_LEVEL,
} from "../../utils/status";
import { getProfile } from "../../api/userApi";

function TransferPage() {
  const { user } = useAuth();
  const userId = user?.user_id;

  const [form, setForm] = useState({
    toIdentifier: "",
    amount: "",
    note: "",
  });

  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);

  const [initialLoading, setInitialLoading] = useState(true); // loading profile + wallet
  const [loading, setLoading] = useState(false); // submit loading

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [amountMessage, setAmountMessage] = useState(""); // inline amount info/warning

  useEffect(() => {
    if (!userId) {
      setError("User not found.");
      setInitialLoading(false);
      return;
    }

    async function loadData() {
      try {
        setInitialLoading(true);
        setError("");

        const [profileRes, walletRes] = await Promise.all([
          getProfile(userId),
          getWalletOverview(userId),
        ]);

        setProfile(profileRes);
        setWallet(walletRes.wallet || walletRes); // support both {wallet: {}} or direct
      } catch (err) {
        console.error(err);
        setError("Failed to load profile or wallet.");
      } finally {
        setInitialLoading(false);
      }
    }

    loadData();
  }, [userId]);

  const status = profile?.status || STATUS_LEVEL.NEW;
  const currentBalance = Number(wallet?.balance) || 0;

  const amountNumber = parseFloat(form.amount) || 0;
  const isAmountEntered = form.amount !== "";

  const fee = amountNumber > 0 ? calculateFee(amountNumber, status) : 0;
  const totalDebit = amountNumber + fee;
  const balanceAfter = currentBalance - totalDebit;

  const canTransfer = canTransferWithdrawAmount(
    amountNumber,
    currentBalance,
    status
  );

  useEffect(() => {
    // Update inline amount message when amount changes
    if (!isAmountEntered || !wallet) {
      setAmountMessage("");
      return;
    }

    if (amountNumber <= 0) {
      setAmountMessage("Amount must be greater than 0.");
      return;
    }

    if (status < STATUS_LEVEL.BRONZE) {
      setAmountMessage(
        "You can transfer money only from Bronze account or higher."
      );
      return;
    }

    if (!canTransfer) {
      setAmountMessage(
        `Invalid amount. You must send more than 5€ and keep at least 5€ in your wallet after fee. Current balance: €${currentBalance.toFixed(
          2
        )}, fee: €${fee.toFixed(2)}, balance after: €${balanceAfter.toFixed(2)}.`
      );
      return;
    }

    // Valid
    setAmountMessage(
      `Fee: €${fee.toFixed(
        2
      )}. Balance after transfer: €${balanceAfter.toFixed(2)}.`
    );
  }, [
    isAmountEntered,
    amountNumber,
    wallet,
    status,
    canTransfer,
    currentBalance,
    fee,
    balanceAfter,
  ]);

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!profile || !wallet) {
      setError("Profile or wallet not loaded yet.");
      return;
    }

    if (status < STATUS_LEVEL.BRONZE) {
      setError("You can transfer money only from Bronze account or higher.");
      return;
    }

    if (!form.toIdentifier) {
      setError("Receiver User Name is required.");
      return;
    }

    if (!amountNumber || amountNumber <= 0) {
      setError("Please enter a valid amount greater than 0.");
      return;
    }

    if (!canTransfer) {
      setError(
        `Invalid amount. You must send more than 5€ and keep at least 5€ in your wallet after fee.`
      );
      return;
    }

    try {
      setLoading(true);

      const payload = {
        transfer_to: form.toIdentifier, // adjust to your backend: maybe to_identifier
        amount: amountNumber,
        note: form.note || undefined,
      };

      const data = await transferMoney(payload, userId); // backend should get sender from token / userId

      setSuccess(data.message || "Money transferred successfully.");
      setForm({
        toIdentifier: "",
        amount: "",
        note: "",
      });

      // refresh wallet to get updated balance
      try {
        const updatedWallet = await getWalletOverview(userId);
        setWallet(updatedWallet.wallet || updatedWallet);
      } catch (err) {
        console.error("Failed to refresh wallet:", err);
      }
    } catch (err) {
      console.error(err);
      setError("Transfer failed. Please check receiver and your balance.");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <div>Loading transfer data...</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="mb-3 animate-fadeIn">
        <Link
          to="/wallet"
          className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
        >
          <FiArrowLeft className="mr-2 h-4 w-4" />
          Back to Wallet
        </Link>
      </div>

      <div className="animate-fadeIn">
        <h1 className="text-3xl font-bold text-gray-900">
          Transfer Money
        </h1>
        <p className="text-gray-600 mt-1">
          Send balance to another user using user Name.
        </p>
      </div>

      {/* Status restriction message */}
      {status < STATUS_LEVEL.BRONZE && (
        <div className="card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 animate-slideUp">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900 mb-1">Transfer Locked</p>
              <p className="text-amber-700">
                Your current account status is{" "}
                <span className="font-medium">{profile?.status || "Unknown"}</span>.{" "}
                You can transfer or withdraw money only when your account is{" "}
                <span className="font-semibold">Bronze</span> or higher (Silver / Gold).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current balance + max transferable */}
      {wallet && (
        <div className="card bg-gradient-to-br from-primary-50 to-secondary-50 border-primary-200 animate-slideUp">
          <div className="flex items-start gap-3">
            <FiDollarSign className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-gray-900 mb-1">
                Current balance: <span className="text-primary-600">€{currentBalance.toFixed(2)}</span>
              </p>
              <p className="text-gray-600">
                Remember: you must send more than 5€ and keep at least 5€ in your wallet after fee.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="card bg-gradient-to-br from-red-50 to-rose-50 border-red-200 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="card bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 animate-fadeIn">
          <div className="flex items-start gap-3">
            <FiCheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-700">{success}</p>
          </div>
        </div>
      )}

      <form className="card-gradient animate-scaleIn space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiUser className="inline-block mr-2 h-4 w-4" />
              Receiver User Name
            </label>
            <TextInput
              name="toIdentifier"
              value={form.toIdentifier}
              onChange={handleChange}
              placeholder="Enter user Name"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiDollarSign className="inline-block mr-2 h-4 w-4" />
              Amount (€)
            </label>
            <TextInput
              name="amount"
              type="number"
              value={form.amount}
              onChange={handleChange}
              placeholder="Enter amount"
              min="0"
              step="0.01"
            />
            {amountMessage && (
              <div className={`flex items-start gap-2 text-xs mt-2 p-3 rounded-lg ${
                canTransfer
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}>
                {canTransfer ? (
                  <FiCheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                ) : (
                  <FiAlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                )}
                <p>{amountMessage}</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiFileText className="inline-block mr-2 h-4 w-4" />
              Note (optional)
            </label>
            <TextInput
              name="note"
              value={form.note}
              onChange={handleChange}
              placeholder="Write a note (optional)"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          loading={loading}
          disabled={loading || status < STATUS_LEVEL.BRONZE || !wallet}
          variant="primary"
        >
          <FiSend className="inline-block mr-2 h-4 w-4" />
          Send Money
        </Button>
      </form>
    </div>
  );
}

export default TransferPage;
